import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Grid } from "@mui/material";
import jwtDecode from "jwt-decode";
import { getFromToDates } from './helpers/helper'
import DashboardHeader from "./components/DashboardHeader";
import DashboardFilters from "./components/DashboardFilters";
import OAuthService from "src/app/auth/services/oAuthService";
import { useDashboardHeader } from "./hooks/useDashboardHeader";
import RetailSalesCard from "./components/cards/RetailSalesCard";
import TotalSalesCard from "./components/cards/TotalSalesCard";
import OrdersPlacedCard from "./components/cards/OrdersPlacedCard";
import SalesProductWise from "./components/cards/SalesProductWise";
import ProjectSalesCard from "./components/cards/ProjectSalesCard";
import SalesPartnerWise from "./components/cards/SalesPartnerWise";
import SalesCategoryWise from "./components/cards/SalesCategoryWise";
import MonthlySalesTable from "./components/cards/MonthlySalesTable";
import YearCompletedCard from "./components/cards/YearCompletedCard";
import SalesCategoryDonut from "./components/cards/SalesCategoryDonut";
import SalesTerritoryWiseTable from "./components/cards/SalesTerritoryWiseTable";
import OrderPlacementProductWise from "./components/cards/OrderPlacementProductWise";
import OrderPlacementCategoryWise from "./components/cards/OrderPlacementCategoryWise";
import SalesAchievedTerritoryWise from "./components/cards/SalesAchievedTerritoryWise";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";


const CommonDashboard = () => {
    const token =
        localStorage.getItem("token") &&
        jwtDecode(localStorage.getItem("token"));

    const [territories, setTerritories] = useState([]);
    const [selectedTerritory, setSelectedTerritory] = useState(null);
    const [filterType, setFilterType] = useState("YEAR");
    const [year, setYear] = useState(() => new Date().getFullYear());
    const [dateRange, setDateRange] = useState({ from: null, to: null });
    const bookingSummary = useDashboardHeader();

    useEffect(() => {
        const loadTerritories = async () => {
            try {
                const territoryUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.territory}`;
                const res = await OAuthService.getList(
                    `${territoryUrl}?size=1000&sort=displayName,asc`
                );

                const list = res?.data?._embedded?.territories ?? [];

                const formatted = list.map((t) => ({
                    id: t.id,
                    name: t.displayName,
                }));

                setTerritories(formatted);

                if (formatted.length > 0 && !selectedTerritory) {
                    setSelectedTerritory(formatted[0].id);
                }
            } catch (e) {
                console.error("Failed to load territories", e);
            }
        };

        loadTerritories();
    }, []);

    const dateParams = useMemo(() => {

        if (filterType === "DATE_RANGE") {
            if (!dateRange?.from || !dateRange?.to) {
                return null;
            }
            const selectedYear = Number(dateRange.from.slice(0, 4));
            if (selectedYear !== year) {
                setYear(selectedYear);
            }
        }

        const dates = getFromToDates({
            filterType,
            year,
            dateRange,
        });
        return {
            ...dates,
            year: dates.from ? dates.from.slice(0, 4) : String(year),
            filterType
        };
    }, [filterType, year, dateRange]);

    const isDateRangeIncomplete =
        filterType === "DATE_RANGE" &&
        (!dateRange?.from || !dateRange?.to);

    const handleTerritoryChange = useCallback((id) => {
        setSelectedTerritory(id);
    }, []);

    return (
        <>
            <div className="px-2 md:px-12 mt-2">
                <DashboardHeader
                    completedUntilWeek={bookingSummary.completedUntilWeek}
                    ongoingWeeks={bookingSummary.ongoingWeeks}
                />
            </div>

            <DashboardFilters
                filterType={filterType}
                setFilterType={setFilterType}
                year={year}
                setYear={setYear}
                dateRange={dateRange}
                setDateRange={setDateRange}
            />
            <Grid container spacing={1} className="px-2 md:px-12">
                <Grid item xs={12} lg={3}>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <OrdersPlacedCard dateParams={dateParams} isDateRangeIncomplete={isDateRangeIncomplete} />
                        </Grid>

                        <Grid item xs={12}>
                            <OrderPlacementProductWise dateParams={dateParams} isDateRangeIncomplete={isDateRangeIncomplete} />
                        </Grid>

                        <Grid item xs={12}>
                            <OrderPlacementCategoryWise dateParams={dateParams} isDateRangeIncomplete={isDateRangeIncomplete} />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12} lg={9}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={3}>
                            <RetailSalesCard
                                dateParams={dateParams}
                                isDateRangeIncomplete={isDateRangeIncomplete}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <ProjectSalesCard
                                dateParams={dateParams}
                                isDateRangeIncomplete={isDateRangeIncomplete}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TotalSalesCard
                                dateParams={dateParams}
                                isDateRangeIncomplete={isDateRangeIncomplete}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <YearCompletedCard year={year} />
                        </Grid>
                    </Grid>

                    <Grid container spacing={1} className="mt-2">
                        <Grid item xs={12} md={6}>
                            <SalesCategoryDonut
                                dateParams={dateParams}
                                isDateRangeIncomplete={isDateRangeIncomplete}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SalesCategoryWise dateParams={dateParams} isDateRangeIncomplete={isDateRangeIncomplete} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SalesProductWise dateParams={dateParams} isDateRangeIncomplete={isDateRangeIncomplete} />
                        </Grid>
                    </Grid>

                    <Grid container spacing={1} className="mt-2">
                        <Grid item xs={12}>
                            <MonthlySalesTable dateParams={dateParams} isDateRangeIncomplete={isDateRangeIncomplete} />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Grid container spacing={1} className="mt-2 px-2 md:px-12">
                <Grid item xs={12} md={8}>
                    <SalesTerritoryWiseTable
                        territories={territories}
                        selectedTerritory={selectedTerritory}
                        onTerritoryChange={handleTerritoryChange}
                        dateParams={dateParams}
                        isDateRangeIncomplete={isDateRangeIncomplete}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <SalesAchievedTerritoryWise
                        selectedTerritory={selectedTerritory}
                        dateParams={dateParams}
                        isDateRangeIncomplete={isDateRangeIncomplete}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={1} className="mt-2 px-2 md:px-12 mb-4">
                <Grid item xs={12} md={12}>
                    <SalesPartnerWise
                        isDateRangeIncomplete={isDateRangeIncomplete}
                        selectedTerritory={selectedTerritory}
                        dateParams={dateParams}
                    />
                </Grid>
            </Grid>

        </>
    );
};

export default CommonDashboard;